import React from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import HPTag, { HPTagColor } from "@components/common/Tag";
import type { ScorecardBrand } from "@models/Scorecard";

interface BrandColumnListItemProps {
  brand: ScorecardBrand;
  isHidden: boolean;
  /** True when this is the only currently visible brand — its toggle is
   *  disabled so at least one column always stays visible. */
  isLastVisible: boolean;
  onToggleVisibility: (id: string) => void;
}

const BrandColumnListItem: React.FC<BrandColumnListItemProps> = ({
  brand,
  isHidden,
  isLastVisible,
  onToggleVisibility,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: brand.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const toggleDisabled = isLastVisible && !isHidden;

  const toggleButton = (
    <IconButton
      size="small"
      aria-label={isHidden ? `Show ${brand.label}` : `Hide ${brand.label}`}
      onClick={() => onToggleVisibility(brand.id)}
      disabled={toggleDisabled}
      sx={{ flexShrink: 0 }}
    >
      {isHidden ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
    </IconButton>
  );

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 1,
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        backgroundColor: isHidden ? "#f5f5f5" : "white",
        opacity: isHidden ? 0.6 : 1,
        cursor: isDragging ? "grabbing" : "default",
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        aria-label={`Drag to reorder ${brand.label}`}
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "grab",
          color: "#666",
          "&:active": { cursor: "grabbing" },
        }}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>

      {toggleDisabled ? (
        <Tooltip title="At least one brand must stay visible">
          <span>{toggleButton}</span>
        </Tooltip>
      ) : (
        toggleButton
      )}

      <Typography
        variant="body2"
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {brand.label}
      </Typography>

      {brand.role === "primary" && (
        <HPTag ariaLabel="Primary Brand" color={HPTagColor.BLUE} hasBorder>
          Primary
        </HPTag>
      )}
    </Box>
  );
};

export default BrandColumnListItem;
